export const WIDGET_SHELL_PALETTES = {
  light: { chatBackground: "#FFFFFF", assistantMessageColor: "#F3F3F3", textColor: "#1B1B1B" },
  dark: { chatBackground: "#111419", assistantMessageColor: "#1E2530", textColor: "#F4F6F8" },
} as const

/** Base tokens from the approved Figma chat. The editor scales these radii as one system. */
export const CHAT_WIDGET_DESIGN_TOKENS = {
  fontFamily: '"DM Sans", "Nunito", Manrope, system-ui, sans-serif',
  radius: {
    shell: 30,
    headerAction: 9,
    avatar: 8,
    callout: 0.8,
    calloutCapsule: 17.87,
    message: 18.6,
    messageTail: 4,
    suggestion: 11.9,
    input: 22.3,
    send: 20.5,
  },
} as const

export function scaleChatRadius(baseRadius: number, shellRadius: number): number {
  const scale = Math.max(0, Math.min(2, shellRadius / CHAT_WIDGET_DESIGN_TOKENS.radius.shell))
  return Math.round(baseRadius * scale * 10) / 10
}

export interface ThemeColors {
  primaryColor: string
  launcherColor: string
  headerColor: string
  userMessageColor: string
  assistantMessageColor: string
  textColor: string
  chatBackground: string
}

export function pickThemeColors(settings: {
  primaryColor: string
  primaryColorDark: string
  launcherColor: string
  launcherColorDark: string
  headerColor: string
  headerColorDark: string
  userMessageColor: string
  userMessageColorDark: string
  assistantMessageColor: string
  assistantMessageColorDark: string
  textColor: string
  textColorDark: string
  chatBackground: string
  chatBackgroundDark: string
}, dark: boolean): ThemeColors {
  return dark
    ? {
        primaryColor: settings.primaryColorDark,
        launcherColor: settings.launcherColorDark,
        headerColor: settings.headerColorDark,
        userMessageColor: settings.userMessageColorDark,
        assistantMessageColor: settings.assistantMessageColorDark,
        textColor: settings.textColorDark,
        chatBackground: settings.chatBackgroundDark,
      }
    : {
        primaryColor: settings.primaryColor,
        launcherColor: settings.launcherColor,
        headerColor: settings.headerColor,
        userMessageColor: settings.userMessageColor,
        assistantMessageColor: settings.assistantMessageColor,
        textColor: settings.textColor,
        chatBackground: settings.chatBackground,
      }
}

const HEX_PATTERN = /^#[0-9a-f]{6}$/i

export const WIDGET_GRADIENT_PRESETS = [
  { name: "Laranja", start: "#EF6F00", end: "#EF9800", angle: 79 },
  { name: "Aurora", start: "#7C3AED", end: "#06B6D4", angle: 135 },
  { name: "Oceano", start: "#2563EB", end: "#22D3EE", angle: 135 },
  { name: "Pôr do sol", start: "#F43F5E", end: "#F97316", angle: 120 },
  { name: "Floresta", start: "#16A34A", end: "#A3E635", angle: 135 },
  { name: "Lavanda", start: "#9333EA", end: "#EC4899", angle: 135 },
  { name: "Menta", start: "#0D9488", end: "#2DD4BF", angle: 135 },
  { name: "Grafite", start: "#334155", end: "#0F172A", angle: 135 },
] as const

export const DEFAULT_LAUNCHER_GRADIENT = {
  start: "#EF6F00",
  end: "#EF9800",
  angle: 79,
} as const

export function resolveLauncherGradient(settings: {
  launcherColor: string
  primaryColor: string
  gradientStart?: string
  gradientEnd?: string
  gradientAngle?: number
}) {
  const hasSavedGradient = settings.gradientStart !== undefined || settings.gradientEnd !== undefined || settings.gradientAngle !== undefined
  const start = settings.gradientStart ?? (hasSavedGradient ? DEFAULT_LAUNCHER_GRADIENT.start : settings.launcherColor)
  const end = settings.gradientEnd ?? (hasSavedGradient ? DEFAULT_LAUNCHER_GRADIENT.end : settings.primaryColor)
  return {
    start: HEX_PATTERN.test(start) ? start : DEFAULT_LAUNCHER_GRADIENT.start,
    end: HEX_PATTERN.test(end) ? end : DEFAULT_LAUNCHER_GRADIENT.end,
    angle: typeof settings.gradientAngle === "number" && Number.isFinite(settings.gradientAngle)
      ? Math.round(Math.max(0, Math.min(360, settings.gradientAngle)))
      : DEFAULT_LAUNCHER_GRADIENT.angle,
  }
}

/** Escolhe preto ou branco para manter contraste legível sobre `hex`. */
export function contrastColor(hex: string): string {
  if (!HEX_PATTERN.test(hex)) return "#FFFFFF"
  const [r, g, b] = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16))
  return (r * 299 + g * 587 + b * 114) / 1000 > 158 ? "#1F2433" : "#FFFFFF"
}

/**
 * Clareia (`amount > 0`) ou escurece (`amount < 0`) um hex RGB.
 * Usado para garantir um segundo tom visível em gradientes.
 */
export function shadeColor(hex: string, amount: number): string {
  if (!HEX_PATTERN.test(hex)) return hex
  const channels = [1, 3, 5].map((i) => Number.parseInt(hex.slice(i, i + 2), 16))
  const adjusted = channels.map((channel) => {
    const value = amount >= 0 ? channel + (255 - channel) * amount : channel * (1 + amount)
    return Math.max(0, Math.min(255, Math.round(value)))
  })
  return `#${adjusted.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
}

/**
 * Monta um degradê legível a partir de duas cores do tema.
 *
 * Bug de produção: vários presets gravam `launcherColor === primaryColor`
 * (Moderno, Profissional, Minimalista, Ardósia…), então o gradiente colapsava
 * em uma cor chapada e o cliente via "o degradê não funciona". Quando as duas
 * pontas coincidem, derivamos um segundo tom para garantir um degradê visível.
 */
export function buildGradient(from: string, to: string, angle = 135, preserveEqualStops = false): string {
  const start = HEX_PATTERN.test(from) ? from : "#000000"
  const normalizedAngle = Number.isFinite(angle) ? Math.round(Math.max(0, Math.min(360, angle))) : 135
  if (!HEX_PATTERN.test(to) || (!preserveEqualStops && to.toLowerCase() === start.toLowerCase())) {
    return `linear-gradient(${normalizedAngle}deg, ${start}, ${shadeColor(start, -0.24)})`
  }
  return `linear-gradient(${normalizedAngle}deg, ${start}, ${to})`
}

export interface SurfaceStyle {
  background?: string
  backgroundColor?: string
  color: string
  borderColor: string
  boxShadow?: string
}

export type LauncherSurfaceStyle = "solid" | "soft" | "gradient" | "outline"

/**
 * Superfície (fundo + cor do ícone/texto) do botão flutuante, em um único lugar,
 * para que o widget real e a pré-visualização do editor não divirjam.
 * A cor do ícone segue o estilo escolhido — não fica presa a uma cor herdada.
 */
export function launcherSurface(
  style: LauncherSurfaceStyle,
  colors: {
    launcherColor: string
    primaryColor: string
    assistantMessageColor: string
    gradientStart?: string
    gradientEnd?: string
    gradientAngle?: number
  },
): SurfaceStyle {
  const { launcherColor, primaryColor, assistantMessageColor } = colors
  if (style === "soft") {
    return {
      backgroundColor: `color-mix(in srgb, ${launcherColor} 13%, white)`,
      color: launcherColor,
      borderColor: `color-mix(in srgb, ${launcherColor} 28%, white)`,
      boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.6)",
    }
  }
  if (style === "gradient") {
    const gradient = resolveLauncherGradient({
      launcherColor,
      primaryColor,
      gradientStart: colors.gradientStart,
      gradientEnd: colors.gradientEnd,
      gradientAngle: colors.gradientAngle,
    })
    return {
      background: buildGradient(gradient.start, gradient.end, gradient.angle, true),
      color: contrastColor(gradient.start),
      borderColor: "transparent",
      boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.6)",
    }
  }
  if (style === "outline") {
    return { backgroundColor: assistantMessageColor, color: launcherColor, borderColor: launcherColor, boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.6)" }
  }
  return { backgroundColor: launcherColor, color: contrastColor(launcherColor), borderColor: "transparent", boxShadow: "0 10px 30px -10px rgba(0, 0, 0, 0.6)" }
}

export function gradientContrastColor(from: string, to: string): string {
  const start = HEX_PATTERN.test(from) ? from : "#000000"
  const end = HEX_PATTERN.test(to) ? to : start
  const whiteContrast = Math.min(contrastRatio(start, "#FFFFFF"), contrastRatio(end, "#FFFFFF"))
  const blackContrast = Math.min(contrastRatio(start, "#000000"), contrastRatio(end, "#000000"))
  return whiteContrast >= blackContrast ? "#FFFFFF" : "#000000"
}

/** WCAG contrast for theme foregrounds, including custom themes. */
export function contrastRatio(a: string, b: string): number {
  const luminance = (hex: string) => {
    if (!HEX_PATTERN.test(hex)) return 0
    const channels = [1, 3, 5].map((index) => {
      const channel = Number.parseInt(hex.slice(index, index + 2), 16) / 255
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    })
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
  }
  const [lighter, darker] = [luminance(a), luminance(b)].sort((left, right) => right - left)
  return (lighter + 0.05) / (darker + 0.05)
}

export function readableTextColor(background: string, preferred: string, minRatio = 4.5): string {
  if (HEX_PATTERN.test(preferred) && contrastRatio(background, preferred) >= minRatio) return preferred
  const useLight = contrastRatio(background, "#FFFFFF") >= contrastRatio(background, "#000000")
  // Preserve the hue of the chosen theme instead of turning every title black or white.
  if (HEX_PATTERN.test(preferred)) {
    for (const amount of [0.12, 0.24, 0.36, 0.48, 0.60, 0.72, 0.84, 0.96]) {
      const adjusted = shadeColor(preferred, useLight ? amount : -amount)
      if (contrastRatio(background, adjusted) >= minRatio) return adjusted
    }
  }
  return useLight ? "#FFFFFF" : "#000000"
}

export function readableGradientStops(background: string, start: string, end: string, minRatio = 4.5) {
  return {
    start: readableTextColor(background, start, minRatio),
    end: readableTextColor(background, end, minRatio),
  }
}

export function midpointColor(from: string, to: string): string {
  if (!HEX_PATTERN.test(from) || !HEX_PATTERN.test(to)) return "#FFFFFF"
  return "#" + [1, 3, 5].map((index) =>
    Math.round((Number.parseInt(from.slice(index, index + 2), 16) + Number.parseInt(to.slice(index, index + 2), 16)) / 2).toString(16).padStart(2, "0"),
  ).join("")
}

export function resolvePrimaryGradient(settings: {
  primaryColor: string
  primaryColorDark: string
  primaryGradientEnabled?: boolean
  primaryGradientStart?: string | null
  primaryGradientEnd?: string | null
  primaryGradientStartDark?: string | null
  primaryGradientEndDark?: string | null
  primaryGradientAngle?: number | null
}, dark: boolean) {
  const primaryColor = dark ? settings.primaryColorDark : settings.primaryColor
  const startSetting = dark ? settings.primaryGradientStartDark : settings.primaryGradientStart
  const endSetting = dark ? settings.primaryGradientEndDark : settings.primaryGradientEnd
  const start = typeof startSetting === "string" && HEX_PATTERN.test(startSetting) ? startSetting : primaryColor
  const defaultEnd = shadeColor(start, contrastColor(start) === "#FFFFFF" ? 0.24 : -0.24)
  const end = typeof endSetting === "string" && HEX_PATTERN.test(endSetting) ? endSetting : defaultEnd
  const angle = typeof settings.primaryGradientAngle === "number" && Number.isFinite(settings.primaryGradientAngle)
    ? Math.round(Math.max(0, Math.min(360, settings.primaryGradientAngle)))
    : 135

  return {
    enabled: settings.primaryGradientEnabled === true,
    start,
    end,
    angle,
    solid: midpointColor(start, end),
  }
}
